<script setup lang="ts">
import { computed, getCurrentInstance } from 'vue';
import LumiIcon from '../icons/LumiIcon.vue';

export type ChipTone = 'default' | 'attention' | 'lavender' | 'danger';

withDefaults(
  defineProps<{
    selected?: boolean;
    removeLabel?: string;
    /** A soft-token wash beside the neutral default — e.g. to mark a chip
     *  that came from typed text rather than a picked option. Tokens only;
     *  `attention` is the Koralle soft/text pair (spec: "shows, does not
     *  judge" — an inline highlight, not a status); `lavender` is the
     *  selection wash (an external party, say); `danger` the error pair
     *  (a value that will not work as it stands). */
    tone?: ChipTone;
  }>(),
  {
    selected: false,
    removeLabel: 'Entfernen',
    tone: 'default',
  },
);

defineEmits<{ remove: [] }>();

// The remove affordance appears only when a listener is attached.
const hasRemoveListener = computed(() => !!getCurrentInstance()?.vnode.props?.onRemove);
</script>

<template>
  <span
    class="lumi-chip"
    :class="{
      'lumi-chip--selected': selected,
      'lumi-chip--attention': tone === 'attention',
      'lumi-chip--lavender': tone === 'lavender',
      'lumi-chip--danger': tone === 'danger',
    }"
  >
    <slot />
    <button
      v-if="hasRemoveListener"
      type="button"
      class="lumi-chip__x"
      :aria-label="removeLabel"
      @click="$emit('remove')"
    >
      <LumiIcon name="remove" width="12" height="12" stroke-width="2.5" />
    </button>
  </span>
</template>

<style>
/* A neutral pill (spec §6) that can be removed: wash on the pill ring, 12 px,
   999 px radius. Selected takes the lavender selection colour. */
.lumi-chip{display:inline-flex;align-items:center;gap:4px;height:26px;padding:0 6px 0 10px;border-radius:var(--radius-pill);border:1px solid var(--pill-ring);background:var(--surface-wash);font-family:var(--font-body);font-size:var(--text-xs);font-weight:var(--weight-medium);color:var(--text-body);line-height:1;white-space:nowrap;}
.lumi-chip:not(:has(.lumi-chip__x)){padding-right:10px;}
.lumi-chip--selected{border-color:var(--info);background:var(--selected-bg);color:var(--info-text);}
.lumi-chip--attention{border-color:var(--attention);background:var(--attention-soft);color:var(--attention-text);}
.lumi-chip--lavender{border-color:var(--panel-edge);background:var(--selected-bg);color:var(--info-text);}
.lumi-chip--danger{border-color:var(--danger);background:var(--danger-soft);color:var(--danger-text);}
.lumi-chip__x{border:none;background:none;padding:0;margin:0;cursor:pointer;color:inherit;opacity:.7;display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:50%;line-height:1;}
.lumi-chip__x:hover{opacity:1;background:var(--pill-ring);}
</style>
